import { Component, Inject, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MarketplaceApp, MarketplaceAppService } from '@galaxy/marketplace-apps/v1';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import {
  Activation,
  ActivationStatus,
  ListVendorAccountMetricsFilterOptions,
  ListVendorAccountMetricsRequest,
  PartnerEngagementApiService,
  VendorAccountMetrics,
} from '@vendasta/marketplace-analytics';
import { FieldMask } from '@vendasta/marketplace-apps/v1';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { filter, map, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { OrderFormSubmissionService } from '../../../core/order-form-submission/order-form-submission.service';
import { VENDOR_ID } from '../../../tokens';
import { OrderFormDialogComponent } from '../../account-order-form-dialog/order-form-dialog.component';
import { PendingAccountsService } from '../../account-pending/pending-accounts.service';
import { ResolveActivationDialogComponent } from '../../account-pending/resolve-dialog/resolve-dialog.component';
import { ToolbarService } from '../../toolbar/toolbar.service';

interface State {
  accounts: VendorAccountMetrics[];
  productData: Map<string, MarketplaceApp>;
  cursor: string;
  hasMore: boolean;
  isResponding: boolean;
  isLoading: boolean;
}

@Component({
  templateUrl: './pending-activations-page.component.html',
  styleUrls: ['./pending-activations-page.component.scss'],
})
export class PendingActivationsPageComponent implements OnInit {
  state$$ = new BehaviorSubject<State>({
    accounts: null,
    productData: new Map<string, MarketplaceApp>(),
    cursor: '',
    hasMore: true,
    isResponding: false,
    isLoading: true,
  });
  state$ = this.state$$.asObservable();
  private pageSize = 10;

  ActivationStatus = ActivationStatus;

  constructor(
    @Inject(VENDOR_ID) private vendorId$: Observable<string>,
    private toolbarService: ToolbarService,
    private engagementService: PartnerEngagementApiService,
    private marketplaceAppService: MarketplaceAppService,
    private orderFormSubmissionService: OrderFormSubmissionService,
    private dialog: MatDialog,
    private snackbarService: SnackbarService,
    private pendingAccountsService: PendingAccountsService,
  ) {}

  ngOnInit(): void {
    this.toolbarService.setConfig({
      title: 'Pending activations',
      backButton: {
        previousPageUrl: '/accounts',
        previousPageTitle: 'Accounts',
      },
      actions: [],
    });
    this.loadMore();
  }

  loadMore(): void {
    this.isLoading = true;
    combineLatest([this.vendorId$, this.state$])
      .pipe(
        take(1),
        filter(([, state]) => state.hasMore),
        switchMap(([vendorId, state]) => {
          return this.engagementService.listVendorAccountMetrics(
            new ListVendorAccountMetricsRequest({
              vendorId: vendorId,
              cursor: state.cursor,
              pageSize: this.pageSize,
              filterOptions: new ListVendorAccountMetricsFilterOptions({
                activationStatuses: [ActivationStatus.ACTIVATION_STATUS_PENDING_ACTIVATION],
                trimActivationsByFilters: true,
              }),
            }),
          );
        }),
        withLatestFrom(this.state$),
        switchMap(([resp, state]) => {
          const newAccounts = resp?.vendorAccountMetrics ?? [];
          return combineLatest([of(state), of(resp), this.getProductData(newAccounts)]);
        }),
      )
      .subscribe(([state, resp, products]) => {
        products.forEach((pd) => state.productData.set(pd.appId, pd));
        this.state$$.next({
          accounts: (state.accounts ?? []).concat(resp?.vendorAccountMetrics ?? []),
          productData: state.productData,
          cursor: resp.nextCursor,
          hasMore: resp.hasMore,
          isResponding: false,
          isLoading: false,
        });
      });
  }

  private getProductData(accounts: VendorAccountMetrics[]): Observable<MarketplaceApp[]> {
    return this.state$.pipe(
      take(1),
      map((state) => state.productData),
      map((productData) => {
        const missingPendingProductIds = accounts
          .map((vam) => vam?.activations ?? [])
          .flat()
          .filter((a) => !productData.has(a.addonId ?? a.appId))
          .map((a) => a.addonId ?? a.appId);
        return Array.from(new Set(missingPendingProductIds));
      }),
      switchMap((missingPendingProductIds) => {
        if (!missingPendingProductIds.length) {
          return of([]);
        }
        return this.marketplaceAppService.getMultiApps(
          missingPendingProductIds,
          new FieldMask({ paths: ['appId', 'name', 'icon'] }),
        );
      }),
    );
  }

  private set isResponding(isResponding: boolean) {
    const state = this.state$$.getValue();
    this.state$$.next({ ...state, isResponding: isResponding });
  }

  private set isLoading(isLoading: boolean) {
    const state = this.state$$.getValue();
    this.state$$.next({ ...state, isLoading: isLoading });
  }

  viewOrderForm(account: VendorAccountMetrics, activation: Activation): void {
    this.isResponding = true;
    let orderFormSubmission$ = this.orderFormSubmissionService.getById(
      activation.appId,
      activation.orderFormSubmissionId,
    );
    if (!activation.orderFormSubmissionId) {
      orderFormSubmission$ = this.orderFormSubmissionService.get(
        account.businessId,
        activation.addonId,
        activation.appId,
        true,
      );
    }
    orderFormSubmission$.pipe(take(1)).subscribe(
      (form) => {
        this.isResponding = false;
        this.dialog.open(OrderFormDialogComponent, { width: '800px', data: form });
      },
      (err) => {
        this.isResponding = false;
        this.snackbarService.openErrorSnack('Error loading order form: ' + err.error.message);
      },
    );
  }

  approveActivation(account: VendorAccountMetrics, activation: Activation, product: MarketplaceApp): void {
    this.isResponding = true;
    const dialogRef = this.dialog.open(ResolveActivationDialogComponent, {
      width: '500px',
      data: {
        title: 'Approve activation?',
        message: `${product.name} will activate for ${account.accountData.companyName}.`,
        confirmationLabel: 'Approve',
        confirmationColor: 'primary',
      },
    });
    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((result: boolean) => {
        if (!result) {
          this.isResponding = false;
          return;
        }

        this.pendingAccountsService
          .resolvePendingActivation(
            activation.activationId,
            activation.appId,
            account.businessId,
            true,
            activation.addonId,
          )
          .subscribe(
            () => {
              this.removeActivation(account, activation);
              this.isResponding = false;
              this.snackbarService.openSuccessSnack('Successfully approved activation');
            },
            (err) => {
              this.isResponding = false;
              this.snackbarService.openErrorSnack('Error approving: ' + err.toString());
            },
          );
      });
  }

  rejectActivation(account: VendorAccountMetrics, activation: Activation, product: MarketplaceApp): void {
    this.isResponding = true;
    const dialogRef = this.dialog.open(ResolveActivationDialogComponent, {
      width: '500px',
      data: {
        title: 'Reject activation?',
        message: `${product.name} will not activate for ${account.accountData.companyName} until a new order is submitted and approved.`,
        inputLabel: 'Provide a rejection reason',
        confirmationLabel: 'Reject',
        confirmationColor: 'warn',
      },
    });
    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((result: string) => {
        if (!result) {
          this.isResponding = false;
          return;
        }

        this.pendingAccountsService
          .resolvePendingActivation(
            activation.activationId,
            activation.appId,
            account.businessId,
            false,
            activation.addonId,
            null,
            result,
          )
          .subscribe(
            () => {
              this.removeActivation(account, activation);
              this.snackbarService.openSuccessSnack('Successfully rejected activation');
              this.isResponding = false;
            },
            (err) => {
              this.snackbarService.openErrorSnack('Error rejecting: ' + err.toString());
              this.isResponding = false;
            },
          );
      });
  }

  private removeActivation(account: VendorAccountMetrics, activation: Activation): void {
    const state = this.state$$.getValue();
    let accounts = state.accounts;
    accounts.forEach((ac) => {
      if (ac.vendorId === account.vendorId && ac.businessId === account.businessId) {
        ac.activations = ac.activations.filter((av) => av.activationId !== activation.activationId);
      }
    });

    // Remove accounts that have been resolved.
    // Then, if necessary, load more.
    // Set accounts to null to show the loading state.
    accounts = accounts.filter((ac) => ac.activations.length > 0);
    if (!accounts.length) {
      accounts = null;
    }
    this.state$$.next({ ...state, accounts: accounts });
    if (!accounts?.length) {
      this.loadMore();
    }
  }
}
